import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import AIStoryGenerator from '@/components/AIStoryGenerator';
import { useElevenLabsTTS } from '@/hooks/useElevenLabsTTS';
import {
  ArrowLeft, Sparkles, Book, Loader2, Volume2, Trash2
} from 'lucide-react';

const StoriesPage = () => {
  const { childId } = useParams();
  const { api } = useAuth();
  const [child, setChild] = useState(null);
  const [savedStories, setSavedStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStory, setSelectedStory] = useState(null);
  const { speak: speakText } = useElevenLabsTTS({ voice: 'nova', speed: 0.9 });

  useEffect(() => {
    fetchData();
  }, [childId]);

  const fetchData = async () => {
    try {
      const [childRes, storiesRes] = await Promise.all([
        api.get(`/children/${childId}`),
        api.get('/stories')
      ]);
      setChild(childRes.data);
      setSavedStories(storiesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const speakStory = (story) => {
    speakText(story.content);
  };

  const handleStoryComplete = (story) => {
    fetchData(); // Refresh saved stories
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-purple-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/learn/${childId}`}>
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-purple-500" />
                <h1 className="font-fredoka text-xl font-bold text-purple-800">Story Time</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Child Info */}
        {child && (
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white text-2xl font-bold mb-2">
              {child.name?.charAt(0).toUpperCase()}
            </div>
            <h2 className="font-fredoka text-xl font-bold text-purple-800">{child.name}'s Stories</h2>
          </div>
        )}

        <Tabs defaultValue="create">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="create" className="gap-2" data-testid="tab-create-story">
              <Sparkles className="w-4 h-4" /> Create Story
            </TabsTrigger>
            <TabsTrigger value="saved" className="gap-2" data-testid="tab-saved-stories">
              <Book className="w-4 h-4" /> Saved Stories ({savedStories.length})
            </TabsTrigger>
          </TabsList>

          {/* Create Story Tab */}
          <TabsContent value="create">
            <AIStoryGenerator 
              childName={child?.name}
              childAge={child?.age || 5}
              onStoryComplete={handleStoryComplete}
            />
          </TabsContent>

          {/* Saved Stories Tab */}
          <TabsContent value="saved">
            {selectedStory ? (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <Button
                  variant="ghost"
                  onClick={() => setSelectedStory(null)}
                  className="mb-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to stories
                </Button>
                <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-amber-800">{selectedStory.title}</CardTitle>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => speakStory(selectedStory)}
                        className="text-amber-600"
                      >
                        <Volume2 className="w-5 h-5" />
                      </Button>
                    </div>
                    {selectedStory.subtitle && (
                      <p className="text-amber-600 text-sm">{selectedStory.subtitle}</p>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-lg max-w-none text-slate-700">
                      {selectedStory.content.split('\n\n').map((p, i) => (
                        <p key={i} className="mb-4">{p}</p>
                      ))}
                    </div>
                    {selectedStory.moral && (
                      <div className="mt-6 p-4 bg-amber-100 rounded-xl">
                        <p className="text-amber-800">
                          <strong>✨ Lesson:</strong> {selectedStory.moral}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ) : savedStories.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Book className="w-16 h-16 mx-auto mb-4 text-purple-300" />
                  <h3 className="text-xl font-bold text-purple-800 mb-2">No Stories Yet</h3>
                  <p className="text-slate-500">Create your first personalized story!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {savedStories.map((story, index) => (
                  <motion.div
                    key={story.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card 
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => setSelectedStory(story)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-bold text-lg text-purple-800">{story.title}</h3>
                            <p className="text-sm text-slate-500">
                              Theme: {story.theme} • Character: {story.character}
                            </p>
                            <p className="text-sm text-slate-400 mt-1">
                              {new Date(story.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              speakStory(story);
                            }}
                            className="text-purple-600"
                          >
                            <Volume2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default StoriesPage;
